import { useLazyQuery, useMutation } from "@apollo/client";
import {
  downloadBase64File,
  GET_TODAY_SHIPPING,
  PRINT_CLINIC_LABEL,
  GET_TODAY_SHIPPING_BY_CLINIC_ID,
  Tabs,
  GET_TODAY_SHIPPING_BY_DELIVERY_CARRIER,
  CarrierType,
} from "@jasper/shared";
import { Text, Button, Group } from "@mantine/core";
import { UserGroup } from "@jasper/shared";
import { useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";

import DisplayShipping from "./components/displayShipping";
import { useShipping } from "@jasper/use-cases";
import { createPDF, downloadFile } from "@jasper/shared";

const ShippingPage = () => {
  const [opened, setOpened] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [todayShipping, setTodayShipping] = useState<UserGroup[]>([]);
  const [todayShippingData, setTodayShippingData] = useState<
    {
      clinicNumber: string;
      carrier: CarrierType;
      orderCount: number;
      shippingCount: number;
    }[]
  >([]);
  const [selectedShippingDate, setSelectedShippingDate] = useState<Date>(
    new Date()
  );

  const { downloadAllCarrierLabels, markAllCarrierShippingAsShipped } =
    useShipping();

  const _TAKE_SHIPPING_PAGINATION_ = 5;

  const [getTodayShippingByClinicId] = useLazyQuery(
    GET_TODAY_SHIPPING_BY_CLINIC_ID,
    { fetchPolicy: "network-only" }
  );

  const [getAllOrdersToShip, { loading }] = useLazyQuery(GET_TODAY_SHIPPING, {
    fetchPolicy: "network-only",
  });
  const [getTodayShippingByDeliveryCarrierGQL] = useLazyQuery(
    GET_TODAY_SHIPPING_BY_DELIVERY_CARRIER,
    {
      fetchPolicy: "network-only",
    }
  );
  const [printClinicLabelMutation] = useMutation(PRINT_CLINIC_LABEL);

  const getAccordionTitle = (userGroup: UserGroup) => {
    return `${userGroup?.clinicNumber} - ${userGroup?.name}`;
  };

  const markAllAsShipped = async (deliveryCarrier: CarrierType) => {
    try {
      notifications.show({
        title: `Marking all ${deliveryCarrier} shipments as shipped`,
        color: "blue",
        message: "",
        loading: true,
        id: "mark-all-as-shipped",
        autoClose: false,
      });
      await markAllCarrierShippingAsShipped({
        variables: {
          carrier: deliveryCarrier,
        },
      });
      notifications.hide("mark-all-as-shipped");
      notifications.show({
        title: "All shipments marked as shipped",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("mark-all-as-shipped");
      notifications.show({
        title: "Error while trying to mark all shipments as shipped",
        color: "red",
        message: "",
      });
    }
  };

  const downloadAllLabels = async (
    deliveryCarrier: CarrierType
  ): Promise<void> => {
    try {
      notifications.show({
        title: `Downloading all ${deliveryCarrier} labels`,
        color: "blue",
        message: "",
        loading: true,
        id: "download-all-clinic-labels",
        autoClose: false,
      });
      const req = await downloadAllCarrierLabels({
        variables: {
          carrier: deliveryCarrier,
        },
      });
      for (const i in req.data?.downloadAllCarrierLabels) {
        downloadBase64File(
          req.data?.downloadAllCarrierLabels[i],
          `${deliveryCarrier}-labels-${new Date().toLocaleString()}-${parseInt(i, 10) + 1}`,
          deliveryCarrier === CarrierType.BIP ? ".pdf" : ".zpl"
        );
      }
      setTodayShipping([]);
      await fetchTodayShipping(selectedShippingDate, 0);
      notifications.hide("download-all-clinic-labels");
      notifications.show({
        title: "All Labels downloaded",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("download-all-clinic-labels");
      notifications.show({
        title: "Error while trying to download all labels",
        color: "red",
        message: "",
      });
    }
  };

  const downloadShippingLabel = async (userGroup: UserGroup) => {
    try {
      notifications.show({
        title: `Downloading labels for ${userGroup.clinicNumber}: ${userGroup.name}`,
        color: "blue",
        message: "",
        loading: true,
        id: "download-clinic-labels",
        autoClose: false,
      });
      const req = await printClinicLabelMutation({
        variables: {
          userGroupId: userGroup?.id,
        },
      });
      (req?.data?.printClinicLabel ?? []).forEach(
        (label: string, index: number) => {
          downloadBase64File(
            label,
            `${userGroup.clinicNumber}-${index}(${new Date().toLocaleDateString()})-label`
          );
        }
      );
      const shippingUpdated = await getTodayShippingByClinicId({
        variables: {
          userGroupId: userGroup.id,
        },
      });
      notifications.hide("download-clinic-labels");
      notifications.show({
        title: "Labels download ok",
        color: "green",
        message: "",
      });
      return shippingUpdated?.data?.getTodayShippingByClinicId ?? [];
    } catch (e) {
      console.error(e);
      notifications.hide("download-clinic-labels");
      notifications.show({
        title: "Error while trying to download clinic-labels",
        color: "red",
        message: "",
      });
    }
  };

  const fetchTodayShipping = async (selectedDate: Date, skip?: number) => {
    const { data } = await getAllOrdersToShip({
      variables: {
        take: _TAKE_SHIPPING_PAGINATION_,
        skip: skip ?? todayShipping.length ?? 0,
        selectedDate: selectedDate,
      },
    });
    setOpened((data?.getTodayShipping ?? []).map(getAccordionTitle));
    setTodayShipping(data?.getTodayShipping ?? []);
    setTodayShippingData(data?.getTodayShippingData ?? []);
  };

  const getTodayShippingByDeliveryCarrier = async (
    deliveryCarrier: CarrierType,
    skip: number,
    filters: {
      orderReference?: string;
      clinicNumber?: string;
      patientFirstname?: string;
      patientLastname?: string;
    }
  ): Promise<UserGroup[]> => {
    try {
      const { data } = await getTodayShippingByDeliveryCarrierGQL({
        variables: {
          take: _TAKE_SHIPPING_PAGINATION_,
          skip: skip,
          deliveryCarrier: deliveryCarrier,
          selectedDate: selectedShippingDate,
          filters: filters,
        },
      });
      return data?.getTodayShippingByDeliveryCarrier ?? [];
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to load more shippings",
        color: "red",
        message: "",
      });
      return [];
    }
  };

  const isTodayDateSelected =
    new Date().toISOString().split("T")[0] ===
    selectedShippingDate.toISOString().split("T")[0];

  const downloadPDF = async (
    todayShippingData: {
      clinicNumber: string;
      carrier: CarrierType;
      shippingCount: number;
      orderCount: number;
    }[]
  ) => {
    const title = `Liste des envois - ${new Date().toLocaleDateString()}`;
    const file = await createPDF({
      columns: [
        { label: "Clinic code", key: "clinicNumber" },
        { label: "Carrier", key: "carrier" },
        { label: "Shippings", key: "shippingCount" },
        { label: "Orders", key: "orderCount" },
      ],
      data: todayShippingData,
      title: title,
    });
    downloadFile(file, title);
  };

  useEffect(() => {
    fetchTodayShipping(selectedShippingDate);
  }, [false]);

  if (loading) {
    return <>Loading...</>;
  }

  return (
    <>
      <input
        type="date"
        value={selectedShippingDate.toISOString().split("T")[0]}
        style={{ margin: "1rem" }}
        onChange={async e => {
          if (!e.target.value) {
            return;
          }
          const selectedDate = new Date(e.target.value);
          setTodayShipping([]);
          await fetchTodayShipping(selectedDate, 0);
          setSelectedShippingDate(selectedDate);
        }}
      />
      {todayShipping.length === 0 && (
        <Text style={{ textAlign: "center", padding: "2rem" }}>
          There are no shippings for the selected date
        </Text>
      )}

      {isTodayDateSelected && (
        <Group justify="center">
          <Button onClick={() => downloadPDF(todayShippingData)}>
            Download PDF
          </Button>
        </Group>
      )}
      <Tabs
        data={todayShipping.map(shipping => {
          return {
            title: shipping.carrier,
            content: (
              <DisplayShipping
                key={shipping.id}
                data={shipping.userGroup ?? []}
                totalCount={shipping.totalCount}
                fileCount={shipping.fileCount}
                opened={opened}
                setOpened={value => setOpened(value)}
                downloadShippingLabel={downloadShippingLabel}
                carrier={shipping.carrier}
                downloadAllLabels={() => downloadAllLabels(shipping.carrier)}
                markAllAsShipped={() => markAllAsShipped(shipping.carrier)}
                getTodayShippingByDeliveryCarrier={
                  getTodayShippingByDeliveryCarrier
                }
                paginationNumber={_TAKE_SHIPPING_PAGINATION_}
                isTodayDateSelected={isTodayDateSelected}
                setSearchText={value => setSearchText(value)}
                searchText={searchText}
              />
            ),
          };
        })}
      />
    </>
  );
};

export default ShippingPage;
